/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './cta-block.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
import C4DContentItem from '../content-item/content-item';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The CTA BLOCK ITEM ROW component
 *
 * @element c4d-cta-block-item-row
 * @slot .
 */
@customElement(`${c4dPrefix}-cta-block-item-row`)
class C4DCTABlockItemRow extends StableSelectorMixin(LitElement) {
  /** Defines if the bottom border is rendered */
  @property({ type: Boolean, reflect: true, attribute: 'no-border' })
  _noBorder = false;
  @state()
  _hasChildWithMedia = false;
  checkForMedia() {
    const childrenWithMedia = Array.from(this.children).filter((elem) => {
      if (elem instanceof C4DContentItem) {
        return (
          (elem as C4DContentItem)?._hasMedia &&
          !(elem as C4DContentItem)?._hasPictogram
        );
      }
      return false;
    });
    this._hasChildWithMedia = childrenWithMedia.length > 0;
    this.render();
  }

  render() {
    const { _hasChildWithMedia: hasChildWithMedia } = this;
    if (hasChildWithMedia) {
      return html`<div class="scroll-container"><slot></slot></div>`;
    } else {
      return html`<slot></slot>`;
    }
  }

  /**
   * Checks if the no-border attribute has changed and applies the border class accordingly
   */
  updated(changedProperties) {
    if (changedProperties.has('_noBorder')) {
      this.classList.toggle(
        `${prefix}--cta-block-item-row__border`,
        !this._noBorder
      );
    }
    this.updateComplete.then(() => {
      this.checkForMedia();
    });
  }

  static get stableSelector() {
    return `${c4dPrefix}--cta-block-item-row`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

console.warn(
  'The cta-block-item-row component has been deprecated in favor of the content-section/block and content-item components. ' +
    'See content-section/block and content-items documentation for more information.'
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DCTABlockItemRow;
